import React, { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, ButtonBase, Divider, IconButton, Tooltip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { COLORS, DeleteDialog, EMPTY_CELL_VALUE, Label14Medium, Loading, useDialogControls } from "components";
import { useGetAllowedValues, useNotify, useSites } from "hooks";
import { EmissionFactorReferencesIcon, EsgDeleteIcon, EsgLeftArrow, SitemultiRegion, EsgNotesIcon } from "assets";
import { convertBlankStringsToNull, getLoggedUserData } from "utils";
import { deleteSiteById, SiteDTO } from "api";
import { SiteAccordion, getAllowedValues, SiteNameDetail, SwitchToActivation } from "./components";
import { AddSiteModal } from "./components/site-editor-form/components/AddSiteModal";
import { SiteColumns } from "./components/site-editor-form/components/SiteColumns";
import { SITES_PAGE } from "./consts";

export const SiteDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const notify = useNotify();
  const deleteDialogControls = useDialogControls();
  const loggedUserData = getLoggedUserData();

  const { data: sitesData, isLoading, refetch } = useSites();
  const { mutateAsync: deleteSiteRequest } = useMutation(deleteSiteById);

  const currentSite = useMemo(() => sitesData?.find((site: SiteDTO) => String(site.id) === params.siteId), [sitesData, params]);
  const userAdminLogged = useMemo(() => loggedUserData.roles?.includes("MODULE_ADMIN") ?? {}, [loggedUserData]);

  const [addSiteModalOpen, setAddSiteModalOpen] = useState(false);
  const [EditSiteOpen, setEditSiteOpen] = useState(false);
  const [EditSite, setEditSite] = useState<any | undefined>();
  const [clickType, setClickType] = useState<string>("");
  const [editNotesOpen, setEditNotesOpen] = useState<boolean>(false);
  const [toDeleteSite, setDeleteSite] = useState(false);

  const handleEmissionFactorReference = (id: number | string) => {
    navigate(`/references?s=${id}`)
  }

  const { data: allowedValues } = useGetAllowedValues();

  const handleCloseSiteModal = () => {
    if (addSiteModalOpen) {
      setAddSiteModalOpen(false);
    }
    if (EditSiteOpen) {
      setEditSiteOpen(false);
    }
    if (editNotesOpen) {
      setEditNotesOpen(false);
    }
  };

  const siteDetailsColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "name",
      header: "Site Name",
      size: 60,
      Cell: ({ cell }: any) => {
        const value = cell.getValue() as string;

        if (!value) {
          return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
        }
        return (
          <Tooltip title={value}>
            <Box
              sx={{
                maxWidth: "160px",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type of Site",
      size: 40,
    },
    {
      accessorKey: "businessUnit",
      header: "Business Unit",
      size: 40,
      Cell: ({ cell }: any) => {
        const value = cell.getValue() as string;
        if (!value) {
          return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
        }
        return (
          <Tooltip title={value}>
            <Box
              sx={{
                maxWidth: "160px",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "city",
      header: "City",
      size: 100,
    },
    {
      accessorKey: "country",
      header: "Country",
      size: 40,
    },
    {
      accessorKey: "countryRegion",
      header: "Region",
      size: 40,
    },
    {
      accessorKey: "region",
      header: "Electricity Region Code",
      Header: <Box>Electricity Region Code</Box>,
      size: 200,
      Cell: ({ row }: any) => {
        const { multipleRegion: regionmultiple, region } = row.original;

        let message = "";
        if (region === null) {
          message = regionmultiple
            ? "Multiple region codes found"
            : "Missing region code";
        }

        return (
          <div
            style={{
              display: "flex",
            }}
          >
            {region === null ? (
              <span style={{ color: "#D86262" }}>
                <SitemultiRegion
                  style={{
                    marginRight: "2px",
                    paddingTop: regionmultiple ? "0px" : "4px",
                  }}
                />
                {message}{" "}
              </span>
            ) : (
              <span>{region} </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "address",
      header: "Street Address",
      size: 40,
      Cell: ({ cell }: any) => {
        const value = cell.getValue() as string;

        if (!value) {
          return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
        }
        return (
          <Tooltip title={value}>
            <Box
              sx={{
                alignItems: "center",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "zipCode",
      header: "ZipCode",
      size: 40,
    },
    // {
    //   accessorKey: "siteArea",
    //   header: "Site Area",
    //   size: 40,
    //   Cell: ({ cell }: any) => {
    //     const value = cell.getValue() as string;

    //     if (!value) {
    //       return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
    //     }
    //     return (
    //       <Tooltip title={value}>
    //         <Box
    //           sx={{
    //             alignItems: "center",
    //             textOverflow: "ellipsis",
    //             whiteSpace: "nowrap",
    //             overflow: "hidden",
    //           }}
    //         >
    //           {value}
    //         </Box>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      accessorKey: "notes",
      header: "Notes",
      size: 40,
    },
  ];

  const handleEditSite = (rowData: any, type: string) => {
    setClickType(type);
    setEditSiteOpen(true);
    setEditSite(rowData);
  };

  const handleEditSiteNotes = (rowData: any, type: string) => {
    setClickType(type)
    setEditNotesOpen(true);
    setEditSite(rowData);
  };

  const onDeleteClick = async (siteId: number | undefined) => {
    if (siteId) {
      try {
        await deleteSiteRequest(siteId, {
          onSuccess: () => {
            deleteDialogControls.close();
            notify.success(`Site successfully deleted: ${currentSite && currentSite.name}!`);
            navigate(SITES_PAGE.ABSOLUTE_PATH);
            refetch();
          },
        });
      } catch (error: any) {
        notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : `Some error has happen while deleting site: ${currentSite && currentSite.name}!`);
        deleteDialogControls.close();
      }
    }
  };

  return (
    isLoading
      ? <Loading />
      : (
        <Box sx={{ mt: 1, mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>
            <ButtonBase sx={{
              display: "flex",
              alignItems: "center",
              color: COLORS.Mainblue,
              gap: 1,
              "&:active": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
              },
            }}
              onClick={() => navigate(-1)}
            >
              <EsgLeftArrow />
              <Label14Medium>Back to Site Management</Label14Medium>
            </ButtonBase>
            <Button
              sx={{
                width: "140px",
                height: "36px",
                color: "#E21515",
                bgcolor: "transparent",
                borderRadius: "8px",
                borderColor: "#E21515",
                fontSize: "14px",
                fontWeight: "500",
              }}
              disabled={!userAdminLogged || !currentSite?.enabled}
              onClick={() => {
                deleteDialogControls.open();
                setDeleteSite(true);
              }}
              startIcon={<EsgDeleteIcon />}
              variant="outlined"
            >
              Delete site
            </Button>
          </Box>
          <Box sx={{ my: 2, px: 3 }}>
            <MaterialReactTable
              columns={SiteColumns}
              data={[currentSite]}
              muiTableContainerProps={{
                sx: {
                  "& thead": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  },
                  "& tbody tr td": {
                    py: 0,
                  },
                  "& thead tr:first-of-type th:first-of-type": {
                    borderRight: "1px solid #e0e0e0",
                    "& > div": {
                      pl: "8px",
                    },
                  },
                  "& tbody tr:first-of-type td:first-of-type": {
                    borderRight: "1px solid #e0e0e0",
                    padding: 0,
                    pl: 2,
                  },
                  "& thead tr:first-of-type th:nth-of-type(2)": {
                    pl: 3,
                  },
                  "& tbody tr:first-of-type td:nth-of-type(2)": {
                    pl: 3,
                  },
                  border: "1px solid #D9D9D9",
                  borderRadius: "10px",
                  minWidth: "100%",
                  width: "100%",
                  maxWidth: "100%",
                  "@media (min-width: 600px)": {
                    width: "100%",
                  },
                  "@media (min-width: 960px)": {
                    width: "1100px",
                  },
                  "@media (min-width: 1280px)": {
                    width: "1100px",
                  },
                  "@media (min-width: 1440px)": {
                    width: "1100px",
                  },
                  "@media (min-width: 1680px)": {
                    width: "1300px",
                  },
                  "@media (min-width: 2400px)": {
                    width: "100%",
                  },
                },
              }}
              muiTablePaperProps={{
                sx: {
                  borderRadius: "10px",
                  boxShadow: "none",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  display: "none",
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  display: "none",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  color: COLORS.Mainblue,
                  lineHeight: "30px",
                  "& .MuiBadge-root": {
                    display: "none",
                  },
                },
              }}
              muiTableHeadRowProps={{
                sx: {
                  height: "45px",
                },
              }}
              initialState={{
                density: "compact",
              }}
            />
          </Box>
          <Divider sx={{ mt: 3, borderColor: "#d9d9d9" }} />
          <Box sx={{ px: 3 }}>
            <p style={{ fontSize: "16px", fontWeight: "500" }}>Site Details</p>
            <MaterialReactTable
              muiTableContainerProps={{
                sx: {
                  "& thead": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  },
                  "& thead tr:first-of-type th:first-of-type": {
                    borderRight: "1px solid #e0e0e0",
                    pr: 0,
                    "& > div": {
                      pl: "8px",
                    },
                  },
                  "& tbody tr:first-of-type td:first-of-type": {
                    borderRight: "1px solid #e0e0e0",
                    pr: 0,
                  },
                  "& thead tr:first-of-type th:nth-of-type(2)": {
                    pl: 3,
                  },
                  "& tbody tr:first-of-type td:nth-of-type(2)": {
                    pl: 3,
                  },
                  border: "1px solid #D9D9D9",
                  borderRadius: "10px",
                  minWidth: "100%",
                  width: "100%",
                  maxWidth: "100%",
                  "@media (min-width: 600px)": {
                    width: "100%",
                  },
                  "@media (min-width: 960px)": {
                    width: "1100px",
                  },
                  "@media (min-width: 1280px)": {
                    width: "1100px",
                  },
                  "@media (min-width: 1440px)": {
                    width: "1100px",
                  },
                  "@media (min-width: 1680px)": {
                    width: "1300px",
                  },
                  "@media (min-width: 2400px)": {
                    width: "100%",
                  },
                },
              }}
              muiTablePaperProps={{
                sx: {
                  borderRadius: "10px",
                  boxShadow: "none",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  display: "none",
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  display: "none",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  color: COLORS.Mainblue,
                  lineHeight: "30px",
                  "& .MuiBadge-root": {
                    display: "none",
                  },
                },
              }}
              muiTableHeadRowProps={{
                sx: {
                  height: "45px",
                  backgroundColor: "#F8FAFC",
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => handleEditSite(convertBlankStringsToNull(row.original), ""),
                sx: {
                  cursor: "pointer",
                  height: "45px",
                  backgroundColor: "white",
                },
              })}
              muiTableBodyCellProps={({ row }) => ({
                sx: {
                  color: row.original?.enabled ? "black" : "grey",
                  fontSize: "14px",
                }
              })}
              columns={siteDetailsColumns}
              data={[currentSite ?? []]}
              enableRowActions
              initialState={{
                density: "compact",
              }}
              renderRowActions={({ row }) => (
                <Box
                  sx={{
                    height: "45px",
                    display: "flex",
                    alignItems: "center",
                    pl: "5px",
                  }}
                >
                  <SwitchToActivation
                    row={row.original}
                    textOn="Deactivate"
                    textOff="Activate"
                    onChange={() => handleEditSite(convertBlankStringsToNull(row.original), row.original.enabled ? "Deactivate" : "Activate")}
                  />
                  <Tooltip arrow placement="top" title="Notes">
                    <IconButton
                      sx={{ width: "35px", height: "35px" }}
                      onClick={() => handleEditSiteNotes(convertBlankStringsToNull(row.original), "")}
                    >
                      <EsgNotesIcon color={row.original.notes !== null ? COLORS.Romance : "grey"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip arrow placement="top" title="Emission Factor References">
                    <IconButton
                      sx={{ width: "35px", height: "35px" }}
                      onClick={() => handleEmissionFactorReference(row.original?.id)}
                    >
                      <EmissionFactorReferencesIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            />
          </Box>
          <Box>
            <SiteAccordion data={currentSite} toDeleteSite={toDeleteSite} />
          </Box>
          <AddSiteModal
            isOpen={addSiteModalOpen || EditSiteOpen || editNotesOpen}
            onClose={handleCloseSiteModal}
            editNotes={editNotesOpen}
            allowedValues={getAllowedValues(allowedValues)}
            editSite={EditSite}
            clickType={clickType}
          />
          <DeleteDialog
            isOpen={deleteDialogControls.isOpen}
            onCancel={deleteDialogControls.close}
            onDeleteClick={() => onDeleteClick(currentSite?.id)}
            target={<SiteNameDetail name={currentSite?.name} />}
            title="Delete Site?"
          />
        </Box>
      )
  )
}